<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="modelValue" class="fixed inset-0 z-50 flex items-center justify-center p-4">
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/50" @click="close" />

        <!-- Modal -->
        <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
          <!-- Header -->
          <div class="p-4 border-b border-gray-200">
            <div class="flex items-center justify-between">
              <h3 class="text-lg font-semibold text-gray-900">
                {{ submission ? 'Edit Grade' : 'Record Grade' }}
              </h3>
              <button
                class="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                @click="close"
              >
                <font-awesome-icon :icon="['fas', 'xmark']" class="text-lg" />
              </button>
            </div>

            <!-- Student & Assignment info -->
            <div class="mt-3 flex items-center gap-3">
              <StudentAvatar :student="student" size="md" :color-index="0" />
              <div>
                <p class="font-medium text-gray-900">{{ getStudentDisplayName(student) }}</p>
                <p class="text-sm text-gray-500">{{ assignment?.assessmentName }}</p>
              </div>
            </div>
          </div>

          <!-- Body -->
          <div class="p-4 space-y-4">
            <!-- Marking type indicator -->
            <div class="text-sm text-gray-500 flex items-center gap-2">
              <font-awesome-icon :icon="['fas', getMarkingIcon()]" />
              <span>{{ getMarkingTypeLabel() }}</span>
            </div>

            <!-- Points input (INTEGER / DECIMAL) -->
            <div v-if="markingType === 'INTEGER' || markingType === 'DECIMAL'">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Score (out of {{ assignment?.markingMethod?.maxPoints ?? 100 }})
              </label>
              <div class="flex items-center gap-2">
                <input
                  v-model.number="pointsValue"
                  type="number"
                  :min="0"
                  :max="assignment?.markingMethod?.maxPoints ?? 100"
                  :step="markingType === 'DECIMAL' ? 0.1 : 1"
                  class="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bc-indigo-500 focus:border-bc-indigo-500"
                  placeholder="Enter score"
                />
                <span class="text-gray-500"
                  >/ {{ assignment?.markingMethod?.maxPoints ?? 100 }}</span
                >
              </div>
              <p
                v-if="pointsValue != null && assignment?.markingMethod?.maxPoints"
                class="mt-1 text-sm text-gray-500"
              >
                {{ Math.round((pointsValue / assignment.markingMethod.maxPoints) * 100) }}%
              </p>
            </div>

            <!-- Percentage input (PERCENT) -->
            <div v-else-if="markingType === 'PERCENT'">
              <label class="block text-sm font-medium text-gray-700 mb-1"> Percentage </label>
              <div class="flex items-center gap-2">
                <input
                  v-model.number="percentValue"
                  type="number"
                  :min="0"
                  :max="100"
                  step="1"
                  class="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bc-indigo-500 focus:border-bc-indigo-500"
                  placeholder="Enter percentage"
                />
                <span class="text-gray-500">%</span>
              </div>
            </div>

            <!-- Scale selection (SCALE) -->
            <div v-else-if="markingType === 'SCALE'">
              <label class="block text-sm font-medium text-gray-700 mb-2"> Select Grade </label>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="level in currentScale?.levels ?? []"
                  :key="level.id"
                  class="px-4 py-2 rounded-lg border-2 text-sm font-medium transition-colors"
                  :class="
                    selectedScaleLevelId === level.id
                      ? 'border-bc-indigo-500 bg-bc-indigo-50 text-bc-indigo-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  "
                  @click="selectedScaleLevelId = level.id"
                >
                  {{ level.text }}
                </button>
              </div>
              <p v-if="selectedScaleLevel" class="mt-2 text-sm text-gray-500">
                Weight: {{ selectedScaleLevel.weight }}
              </p>
            </div>

            <!-- Comment -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Comment (optional)
              </label>
              <textarea
                v-model="comment"
                rows="2"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bc-indigo-500 focus:border-bc-indigo-500 resize-none"
                placeholder="Add a comment..."
              />
            </div>

            <!-- Error message -->
            <div v-if="errorMessage" class="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p class="text-sm text-red-600">{{ errorMessage }}</p>
            </div>
          </div>

          <!-- Footer -->
          <div class="p-4 border-t border-gray-200 flex items-center justify-between">
            <AppButton
              v-if="submission"
              variant="primary"
              size="md"
              :disabled="saving"
              class="!bg-transparent !text-red-600 hover:!bg-red-50"
              @click="handleDelete"
            >
              <font-awesome-icon :icon="['fas', 'trash']" class="mr-2" />
              Remove
            </AppButton>
            <div v-else />

            <div class="flex items-center gap-2">
              <AppButton variant="secondary" size="md" :disabled="saving" @click="close">
                Cancel
              </AppButton>
              <AppButton
                variant="primary"
                size="md"
                :disabled="!isValid"
                :loading="saving"
                @click="handleSave"
              >
                {{ submission ? 'Update' : 'Save' }}
              </AppButton>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import {
  gradebookApi,
  type GradebookStudent,
  type GradebookAssignment,
  type GradebookSubmission,
  type Scale,
  type MarkingType,
} from '@/api'
import { getStudentDisplayName } from '@/utils/student'
import StudentAvatar from '@/components/StudentAvatar.vue'
import { AppButton } from '@/design-system/components'
import { useAnalytics } from '@/composables/useAnalytics'

const props = defineProps<{
  modelValue: boolean
  student: GradebookStudent | null
  assignment: GradebookAssignment | null
  submission: GradebookSubmission | null
  scales: Scale[]
  /** Where the grade was entered — 'gradebook' | 'assignment'. */
  source?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  saved: []
  deleted: []
}>()

const { capture } = useAnalytics()

// Form state
const pointsValue = ref<number | null>(null)
const percentValue = ref<number | null>(null)
const selectedScaleLevelId = ref<string | null>(null)
const comment = ref('')
const saving = ref(false)
const errorMessage = ref<string | null>(null)

// Computed
const markingType = computed<MarkingType | null>(() => {
  return props.assignment?.markingMethod?.type ?? null
})

const currentScale = computed(() => {
  const scaleId = props.assignment?.markingMethod?.scaleId
  if (!scaleId) return null
  return props.scales.find((s) => s.id === scaleId) ?? null
})

const selectedScaleLevel = computed(() => {
  if (!selectedScaleLevelId.value || !currentScale.value) return null
  return currentScale.value.levels.find((l) => l.id === selectedScaleLevelId.value) ?? null
})

const isValid = computed(() => {
  switch (markingType.value) {
    case 'INTEGER':
    case 'DECIMAL':
      return pointsValue.value != null && pointsValue.value >= 0
    case 'PERCENT':
      return percentValue.value != null && percentValue.value >= 0 && percentValue.value <= 100
    case 'SCALE':
      return selectedScaleLevelId.value != null
    default:
      return false
  }
})

// Methods
function getMarkingIcon(): string {
  switch (markingType.value) {
    case 'INTEGER':
    case 'DECIMAL':
      return 'hashtag'
    case 'PERCENT':
      return 'percent'
    case 'SCALE':
      return 'list-ol'
    default:
      return 'check'
  }
}

function getMarkingTypeLabel(): string {
  switch (markingType.value) {
    case 'INTEGER':
      return 'Points (whole numbers)'
    case 'DECIMAL':
      return 'Points (decimals allowed)'
    case 'PERCENT':
      return 'Percentage'
    case 'SCALE':
      return `Scale: ${currentScale.value?.name ?? 'Unknown'}`
    default:
      return 'Unknown marking type'
  }
}

function resetForm() {
  pointsValue.value = null
  percentValue.value = null
  selectedScaleLevelId.value = null
  comment.value = ''
  errorMessage.value = null
}

function populateFromSubmission() {
  if (!props.submission?.marking) {
    resetForm()
    return
  }

  const marking = props.submission.marking
  switch (marking.type) {
    case 'INTEGER':
    case 'DECIMAL':
      pointsValue.value = marking.score ?? null
      break
    case 'PERCENT':
      percentValue.value = marking.percent ?? null
      break
    case 'SCALE':
      selectedScaleLevelId.value = marking.level?.id ?? null
      break
  }

  // Load comment from first comment if exists
  const firstComment = props.submission.comments?.[0]
  if (firstComment) {
    comment.value = firstComment.body ?? ''
  }
}

function close() {
  emit('update:modelValue', false)
}

async function handleSave() {
  if (!props.student || !props.assignment) return

  saving.value = true
  errorMessage.value = null

  try {
    const data: {
      assignmentId: string
      studentId: string
      numericPoints?: number
      scaleLevelId?: string
      comment?: string
    } = {
      assignmentId: props.assignment.id,
      studentId: props.student.id,
    }

    switch (markingType.value) {
      case 'INTEGER':
      case 'DECIMAL':
        data.numericPoints = pointsValue.value ?? undefined
        break
      case 'PERCENT':
        // Backend expects numeric points, convert percent to points
        // For percent type, the "max" is effectively 100
        data.numericPoints = percentValue.value ?? undefined
        break
      case 'SCALE':
        data.scaleLevelId = selectedScaleLevelId.value ?? undefined
        break
    }

    if (comment.value.trim()) {
      data.comment = comment.value.trim()
    }

    await gradebookApi.upsertSubmission(data)
    capture('grade_recorded', { source: props.source ?? 'gradebook' })
    emit('saved')
    close()
  } catch (err: unknown) {
    console.error('Failed to save grade:', err)
    errorMessage.value = 'Failed to save grade. Please try again.'
  } finally {
    saving.value = false
  }
}

async function handleDelete() {
  if (!props.submission) return

  saving.value = true
  errorMessage.value = null

  try {
    await gradebookApi.deleteSubmission(props.submission.id)
    emit('deleted')
    close()
  } catch (err: unknown) {
    console.error('Failed to delete grade:', err)
    errorMessage.value = 'Failed to delete grade. Please try again.'
  } finally {
    saving.value = false
  }
}

// Watch for modal open to populate/reset form
watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      populateFromSubmission()
    } else {
      resetForm()
    }
  }
)

// Watch for submission changes while modal is open
watch(
  () => props.submission,
  () => {
    if (props.modelValue) {
      populateFromSubmission()
    }
  }
)
</script>
